import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import PageBanner from '../components/PageBanner';
import Reveal from '../components/ui/Reveal';
import usePage from '../hooks/usePage';
import { UPLOADS_URL } from '../api/axios';

const resolveImage = (src, fallback = '') => {
  if (!src) return fallback;
  if (src.startsWith('http://') || src.startsWith('https://') || src.startsWith('/')) return src;
  return `${UPLOADS_URL}/${src}`;
};

const LEADERSHIP_PILLARS = [
  {
    icon: 'fa-seedling',
    title: 'Nurturing Potential',
    text: 'Every child carries a unique spark of curiosity and wonder. We create an inclusive space where individual talents are discovered, celebrated, and guided.',
  },
  {
    icon: 'fa-brain',
    title: 'Holistic Exploration',
    text: 'Education is more than scoring marks. We blend scholastic rigour with athletics, arts, life skills, and digital literacy for real-world confidence.',
  },
  {
    icon: 'fa-hand-holding-heart',
    title: 'Compassionate Leadership',
    text: 'We prepare students to make thoughtful decisions, show empathy to peers, and grow into responsible global citizens with high moral standards.',
  },
];

const PrincipalMessage = () => {
  const pageData = usePage('principals-message');
  const sections = pageData.sections || {};

  const banner = sections.banner || pageData.banner || {};
  const bannerTitle = banner.title || pageData.title || "Principal's Desk";
  const bannerSubtitle = banner.subtitle || "A Word from Our Principal, Ms. Nidhi Jain";
  const bannerBreadcrumb = banner.breadcrumb || "PRINCIPAL'S MESSAGE";
  const bannerImgSrc = pageData.bannerImage || '/images/page-banners/principal-message.jpg';

  const principalName = sections.principal_name || 'Ms. Nidhi Jain';
  const principalTitle = sections.principal_title || 'Principal, St. Andrews World School, Sonipat';
  const principalImg = resolveImage(
    sections.principal_image || pageData.featuredImage,
    '/images/page-banners/principal-message.jpg'
  );

  const leadQuote =
    sections.lead_quote ||
    'Building Foundations. Nurturing Possibilities. Shaping Futures.';

  const rawParagraphs =
    sections.paragraphs ||
    "At St. Andrews World School, Sonipat, we believe that every child deserves the opportunity to learn, grow, and shine. Our approach to education goes beyond academics, focusing on the overall development of each student by nurturing their intellectual, emotional, physical, and social abilities.\n\nWe strive to provide a supportive and inspiring learning environment where curiosity is encouraged, creativity is celebrated, and individual talents are recognised. Through innovative teaching methods, meaningful activities, and strong moral values, we prepare our students to face future challenges with confidence and responsibility.\n\nOur committed educators guide every child with care and encouragement, helping them build essential life skills, develop independent thinking, and discover their unique strengths. We aim to make learning a joyful experience that inspires students to become lifelong learners and responsible global citizens.\n\nI warmly welcome you to St. Andrews World School, Sonipat, and invite you to discover our commitment to excellence in education. Together, let us nurture young minds and empower them to build a bright and successful future.";

  const paragraphs = useMemo(() => {
    return String(rawParagraphs)
      .replace(/\r/g, '')
      .split(/\n\s*\n|\n/)
      .map((p) => p.trim())
      .filter(Boolean);
  }, [rawParagraphs]);

  const closingQuote =
    sections.closing_quote ||
    '“Education is the most powerful weapon which you can use to change the world.”';

  return (
    <>
      <SEO
        slug="principals-message"
        title={pageData.title || "Principal's Desk"}
        description="A message from the Principal of St. Andrews World School, Sonipat — vision, educational values, and student care."
        keywords="Principal Message, Principal Desk, Ms Nidhi Jain, St Andrews Sonipat, School Leadership"
      />

      {/* ─── HERO BANNER ─────────────────────────────────────────── */}
      <PageBanner
        title={bannerTitle}
        subtitle={bannerSubtitle}
        breadcrumb={bannerBreadcrumb}
        image={bannerImgSrc}
        fallback="/images/page-banners/principal-message.jpg"
      />

      {/* ─── SECTION 1: LEADERSHIP ADDRESS & PORTRAIT (75% on desktop) ─── */}
      <section className="bg-white py-14 md:py-20">
        <div className="w-full lg:w-[75%] lg:max-w-[75%] mx-auto px-5 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
            {/* STICKY OFFICIAL PORTRAIT & PROFILE CARD */}
            <div className="lg:col-span-5 xl:col-span-4 lg:sticky lg:top-28">
              <Reveal delayMs={50}>
                {/* Framed Portrait Box */}
                <div className="relative p-3.5 bg-[#f9f8f4] border border-gray-200 shadow-md group">
                  <div className="aspect-[3/4] bg-gray-100 overflow-hidden relative">
                    <img
                      src={principalImg}
                      alt={principalName}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      loading="lazy"
                      onError={(e) => {
                        e.target.src = '/images/page-banners/principal-message.jpg';
                      }}
                    />
                  </div>

                  {/* Decorative Floating Quote Seal */}
                  <div className="absolute -bottom-5 -right-5 w-14 h-14 md:w-16 md:h-16 bg-[#1b3160] rounded-full text-white border-4 border-white shadow-xl flex items-center justify-center">
                    <i className="fas fa-quote-right text-lg md:text-xl text-[#d8a85f]"></i>
                  </div>
                </div>

                {/* Profile Placard */}
                <div className="mt-7 p-6 bg-white border border-gray-100 shadow-sm text-center">
                  <h3 className="text-2xl font-bold text-[#1b3160] font-serif mb-1">
                    {principalName}
                  </h3>
                  <p className="text-[#d8a85f] text-xs font-bold uppercase tracking-widest">
                    {principalTitle}
                  </p>
                  <div className="w-12 h-0.5 bg-[#d8a85f] mx-auto my-4"></div>

                  {/* 3 Quick Leadership Badges */}
                  <div className="space-y-2.5 text-left text-xs text-gray-600 mb-5">
                    <div className="flex items-center gap-2.5">
                      <span className="w-6 h-6 rounded-full bg-[#1b3160]/10 text-[#1b3160] flex items-center justify-center shrink-0 text-[11px]">
                        <i className="fas fa-certificate"></i>
                      </span>
                      <span className="font-medium">CBSE Curriculum Excellence</span>
                    </div>
                    <div className="flex items-center gap-2.5">
                      <span className="w-6 h-6 rounded-full bg-[#1b3160]/10 text-[#1b3160] flex items-center justify-center shrink-0 text-[11px]">
                        <i className="fas fa-heart"></i>
                      </span>
                      <span className="font-medium">Holistic & Value-Driven Growth</span>
                    </div>
                    <div className="flex items-center gap-2.5">
                      <span className="w-6 h-6 rounded-full bg-[#1b3160]/10 text-[#1b3160] flex items-center justify-center shrink-0 text-[11px]">
                        <i className="fas fa-child-reaching"></i>
                      </span>
                      <span className="font-medium">Child-Centric Learning Framework</span>
                    </div>
                  </div>
                </div>
              </Reveal>
            </div>

            {/* MAIN LETTER & ADDRESS */}
            <div className="lg:col-span-7 xl:col-span-8">
              <Reveal>
                <div className="mb-6">
                  <span className="text-[#d8a85f] text-xs font-bold uppercase tracking-[0.2em] block mb-2">
                    From the Principal's Desk
                  </span>
                  <h2 className="text-3xl sm:text-4xl lg:text-[42px] font-bold text-[#1b3160] leading-tight font-serif">
                    Welcome to St. Andrews World School
                  </h2>
                  <div className="w-16 h-1 bg-[#d8a85f] mt-4 mb-6 rounded-full"></div>
                </div>

                {/* Lead Callout Quote Box */}
                {leadQuote && (
                  <div className="bg-[#f9f8f4] border-l-4 border-[#d8a85f] p-5 md:p-6 mb-8 rounded-r-xl shadow-sm">
                    <p className="font-serif italic text-lg md:text-xl text-[#1b3160] font-medium leading-relaxed">
                      “{leadQuote}”
                    </p>
                  </div>
                )}

                {/* Body Paragraphs with drop cap styling on first paragraph */}
                <div className="space-y-5 text-gray-700 text-[16px] md:text-[17px] leading-[1.8] font-normal">
                  {paragraphs.map((p, idx) => (
                    <p
                      key={idx}
                      className={
                        idx === 0
                          ? 'first-letter:float-left first-letter:text-5xl first-letter:pr-3 first-letter:font-serif first-letter:font-bold first-letter:text-[#1b3160] first-letter:leading-none'
                          : ''
                      }
                    >
                      {p}
                    </p>
                  ))}
                </div>

                {/* Editorial Sign-off Block */}
                <div className="mt-10 pt-6 border-t border-gray-200/80">
                  <p className="text-gray-500 text-sm mb-1 italic">
                    With warm regards,
                  </p>
                  <div className="font-serif text-2xl md:text-3xl font-bold text-[#1b3160] mt-1">
                    {principalName}
                  </div>
                  <div className="text-[#d8a85f] text-xs md:text-sm font-semibold uppercase tracking-wider mt-0.5">
                    {principalTitle}
                  </div>
                  <div className="text-gray-400 text-xs mt-1">
                    St. Andrews World School, Sonipat
                  </div>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* ─── SECTION 2: LEADERSHIP PILLARS ──────────────────────── */}
      <section className="bg-[#f9f8f4] py-16 md:py-20">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <div className="text-center max-w-3xl mx-auto mb-12">
              <span className="text-[#d8a85f] text-xs font-bold uppercase tracking-[0.2em] block mb-2">
                Our Educational Ethos
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-[#1b3160] font-serif">
                Guiding Principles of Our Leadership
              </h2>
              <div className="w-16 h-1 bg-[#d8a85f] mx-auto mt-4 rounded-full"></div>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {LEADERSHIP_PILLARS.map((item, idx) => (
              <Reveal key={idx} delayMs={idx * 60} className="h-full">
                <div className="group h-full bg-white p-8 border border-gray-200 transition-all duration-300 hover:border-[#d8a85f] hover:shadow-xl hover:-translate-y-1.5 flex flex-col">
                  <div className="w-14 h-14 rounded-2xl bg-[#1b3160]/10 text-[#1b3160] flex items-center justify-center mb-6 group-hover:bg-[#1b3160] group-hover:text-[#d8a85f] transition-all duration-300">
                    <i className={`fas ${item.icon} text-2xl`}></i>
                  </div>
                  <h3 className="font-serif font-bold text-2xl text-[#1b3160] mb-3 group-hover:text-[#d8a85f] transition-colors">
                    {item.title}
                  </h3>
                  <div className="w-10 h-0.5 bg-[#d8a85f] mb-4"></div>
                  <p className="text-gray-600 text-sm leading-relaxed flex-1">
                    {item.text}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ─── SECTION 3: INSPIRATIONAL CLOSING QUOTE BANNER ──────── */}
      <section className="bg-white py-14 md:py-20 border-t border-gray-100">
        <div className="mx-auto max-w-4xl px-5 lg:px-8 text-center">
          <Reveal>
            <div className="w-16 h-16 rounded-full bg-[#f9f8f4] border border-gray-200 text-[#d8a85f] flex items-center justify-center mx-auto mb-6 shadow-sm">
              <i className="fas fa-quote-left text-2xl"></i>
            </div>
            <blockquote className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#1b3160] font-serif italic leading-relaxed">
              {closingQuote}
            </blockquote>
            <div className="w-16 h-0.5 bg-[#d8a85f] mx-auto my-6"></div>
            <p className="text-gray-500 text-xs text-center md:text-sm font-bold uppercase tracking-widest">
              Guided by Lifelong Values & Institutional Excellence
            </p>
          </Reveal>
        </div>
      </section>
    </>
  );
};

export default PrincipalMessage;
